import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import Hero from '@/components/sections/Hero';
import ServicesSection from '@/components/sections/ServicesSection';
import TestimonialsSection from '@/components/sections/TestimonialsSection';
import CitiesSection from '@/components/sections/CitiesSection';
import FAQSection from '@/components/sections/FAQSection';
import ContactForm from '@/components/sections/ContactForm';
import QuoteForm from '@/components/sections/QuoteForm';
import { SITE_CONFIG } from '@/data/siteData';
import { FR_SEO_SECTION_LINKS } from '@/data/frSeoPages';
import { SERVICES_SEO_SECTION_LINKS } from '@/data/servicesSeoPages';

// Local SEO Text Block
const LocalSEOBlock = () => {
  const { t } = useLanguage();
  
  return (
    <section className="py-12 bg-[#f8f5f0]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
          <p>
            {t(
              "VS Riviera Conciergerie propose des services professionnels de ",
              "VS Riviera Conciergerie offers professional "
            )}
            <Link to="/seo/menage-saint-raphael" className="text-[#D4AF37] hover:underline">
              {t("ménage à Saint-Raphaël", "housekeeping in Saint-Raphaël")}
            </Link>
            {t(", ", ", ")}
            <Link to="/seo/menage-frejus" className="text-[#D4AF37] hover:underline">
              {t("ménage à Fréjus", "housekeeping in Fréjus")}
            </Link>
            {t(", Sainte-Maxime, Saint-Aygulf et Cannes.", ", Sainte-Maxime, Saint-Aygulf and Cannes.")}
          </p>
          <p className="mt-4">
            {t(
              "Nous intervenons pour le ménage d'appartements, le ",
              "We provide apartment cleaning, "
            )}
            <Link to="/seo/nettoyage-villa-sainte-maxime" className="text-[#D4AF37] hover:underline">
              {t("nettoyage de villas", "villa cleaning")}
            </Link>
            {t(", l'entretien des ", ", ")}
            <Link to="/seo/menage-airbnb-saint-raphael" className="text-[#D4AF37] hover:underline">
              {t("locations Airbnb", "Airbnb rental maintenance")}
            </Link>
            {t(" et le ", " and ")}
            <Link to="/seo/nettoyage-apres-chantier" className="text-[#D4AF37] hover:underline">
              {t("nettoyage après travaux", "post-construction cleaning")}
            </Link>
            {t(".", ".")}
          </p>
          <p className="mt-4">
            {t(
              "Notre équipe intervient rapidement sur toute la Côte d'Azur, incluant le ",
              "Our team operates quickly throughout the French Riviera, including "
            )}
            <Link to="/seo/nettoyage-appartement-cannes" className="text-[#D4AF37] hover:underline">
              {t("nettoyage d'appartement à Cannes", "apartment cleaning in Cannes")}
            </Link>
            {t(" et le ", " and ")}
            <Link to="/seo/nettoyage-villa-saint-tropez" className="text-[#D4AF37] hover:underline">
              {t("nettoyage de villa à Saint-Tropez", "villa cleaning in Saint-Tropez")}
            </Link>
            {t(", afin de garantir un service fiable et professionnel.", ", to ensure reliable and professional service.")}
          </p>
        </div>
      </div>
    </section>
  );
};

// City Services Section - Links to /fr/ pages
const CityServicesSection = () => {
  const { t } = useLanguage();
  
  const frejusLinks = FR_SEO_SECTION_LINKS.filter(l => l.href.includes('frejus'));
  const saintRaphaelLinks = FR_SEO_SECTION_LINKS.filter(l => l.href.includes('saint-raphael'));
  const cannesLinks = FR_SEO_SECTION_LINKS.filter(l => l.href.includes('cannes'));
  
  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="text-2xl sm:text-3xl font-bold text-[#1a365d] mb-8 text-center"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          {t("Services de ménage et nettoyage par ville", "Cleaning services by city")}
        </h2>
        
        <div className="grid md:grid-cols-3 gap-8">
          {/* Fréjus */}
          <div className="bg-[#f8f5f0] rounded-xl p-6">
            <h3 className="text-lg font-semibold text-[#1a365d] mb-4">Fréjus</h3>
            <ul className="space-y-2">
              {frejusLinks.map((link, i) => (
                <li key={i}>
                  <Link to={link.href} className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Saint-Raphaël */}
          <div className="bg-[#f8f5f0] rounded-xl p-6">
            <h3 className="text-lg font-semibold text-[#1a365d] mb-4">Saint-Raphaël</h3>
            <ul className="space-y-2">
              {saintRaphaelLinks.map((link, i) => (
                <li key={i}>
                  <Link to={link.href} className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Cannes */}
          <div className="bg-[#f8f5f0] rounded-xl p-6">
            <h3 className="text-lg font-semibold text-[#1a365d] mb-4">Cannes</h3>
            <ul className="space-y-2">
              {cannesLinks.map((link, i) => (
                <li key={i}>
                  <Link to={link.href} className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// Collapsible City Menu Component
const CityAccordion = ({ city, links, isOpen, onToggle }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
        data-testid={`accordion-${city.toLowerCase().replace(/[^a-z]/g, '-')}`}
      >
        <h3 className="text-lg font-semibold text-[#1a365d]">{city}</h3>
        <span className={`text-[#D4AF37] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </span>
      </button>
      <div 
        className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <ul className="px-6 pb-4 space-y-2">
          {links.map((link, i) => (
            <li key={i}>
              <Link 
                to={link.href} 
                className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors block py-1"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// Additional Cleaning Services Section - Links to 19 new pages with accordion
const AdditionalCleaningServicesSection = () => {
  const { t } = useLanguage();
  const [openCity, setOpenCity] = React.useState(null);
  
  // Group by city
  const frejusLinks = SERVICES_SEO_SECTION_LINKS.filter(l => l.city === 'Fréjus');
  const saintRaphaelLinks = SERVICES_SEO_SECTION_LINKS.filter(l => l.city === 'Saint-Raphaël');
  const sainteMaximeLinks = SERVICES_SEO_SECTION_LINKS.filter(l => l.city === 'Sainte-Maxime');
  
  const cities = [
    { name: 'Fréjus', links: frejusLinks },
    { name: 'Saint-Raphaël', links: saintRaphaelLinks },
    { name: 'Sainte-Maxime', links: sainteMaximeLinks }
  ];
  
  const handleToggle = (cityName) => {
    setOpenCity(openCity === cityName ? null : cityName);
  };
  
  return (
    <section className="py-16 bg-[#f8f5f0]" data-testid="additional-cleaning-services">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="text-2xl sm:text-3xl font-bold text-[#1a365d] mb-8 text-center"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          {t("Services supplémentaires de nettoyage", "Additional Cleaning Services")}
        </h2>
        
        <div className="space-y-4">
          {cities.map((city) => (
            <CityAccordion
              key={city.name}
              city={city.name}
              links={city.links}
              isOpen={openCity === city.name}
              onToggle={() => handleToggle(city.name)}
            />
          ))}
        </div>
        
        {/* Hidden links for SEO - always rendered in DOM for Google crawlers */}
        <nav className="sr-only" aria-label="Services supplémentaires de nettoyage">
          {cities.map((city) => (
            <div key={city.name}>
              <h3>{city.name}</h3>
              <ul>
                {city.links.map((link, i) => (
                  <li key={i}>
                    <Link to={link.href}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
    </section>
  );
};

const HomePage = () => {
  const { lang, t } = useLanguage();

  const title = t(
    'VS Riviera Conciergerie | Nettoyage Premium & Gestion Airbnb Côte d\'Azur',
    'VS Riviera Conciergerie | Premium Cleaning & Airbnb Management French Riviera'
  );

  const description = t(
    "Services premium de nettoyage, gestion locative Airbnb et conciergerie sur la Côte d'Azur. Saint-Raphaël, Fréjus, Cannes, Sainte-Maxime. Excellence et discrétion.",
    "Premium cleaning, Airbnb property management and concierge services on the French Riviera. Saint-Raphaël, Fréjus, Cannes, Sainte-Maxime. Excellence and discretion."
  );

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={lang === 'en' ? `${window.location.origin}/en` : window.location.origin} />
        <link rel="alternate" hreflang="fr" href={window.location.origin} />
        <link rel="alternate" hreflang="en" href={`${window.location.origin}/en`} />
        <link rel="alternate" hreflang="x-default" href={window.location.origin} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />
      
      <main data-testid="homepage">
        <Hero />
        <ServicesSection />
        <CityServicesSection />
        <AdditionalCleaningServicesSection />
        <QuoteForm />
        <TestimonialsSection />
        <CitiesSection />
        <LocalSEOBlock />
        <ContactForm />
        <FAQSection />
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default HomePage;
